import { useState } from 'react';
import { Save, Plus, Trash2, AlertCircle, Check } from 'lucide-react';
import { pb, errorText } from '../../lib/pb';
import { useCollection } from '../../lib/hooks';
import type { RecordModel } from 'pocketbase';

interface SettingRecord extends RecordModel {
  key: string;
  value: unknown;
}

/**
 * Admin-only price list. Costs are charged when a generation COMPLETES (kie.ai
 * only bills finished generations), at the price stored at that moment — so
 * editing a price here changes future generations only, and historic spend
 * stays what was actually paid. The statistics themselves live in the public
 * Statistics tab; only the per-model prices are restricted to admins (the
 * settings collection is admin-only).
 */
export default function ModelCosts() {
  const settings = useCollection<SettingRecord>('settings', { filter: 'key = "costs"' });
  const record = settings.items[0];

  const [rows, setRows] = useState<Array<{ model: string; price: string }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor state is derived from the record on first render after load.
  const current =
    rows ??
    Object.entries((record?.value as Record<string, number>) ?? {}).map(([model, price]) => ({
      model,
      price: String(price),
    }));

  const save = async () => {
    if (!record) return;
    const value: Record<string, number> = {};
    for (const row of current) {
      const model = row.model.trim();
      if (!model) continue;
      const price = Number(row.price);
      if (!Number.isFinite(price) || price < 0) {
        setError(`"${model}": price must be a number ≥ 0`);
        return;
      }
      value[model] = price;
    }
    setBusy(true);
    setError(null);
    try {
      await pb.collection('settings').update(record.id, { value });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await settings.reload();
      setRows(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (settings.loading) {
    return <div className="zen-card p-6 text-xs text-natural-text/45">Loading…</div>;
  }
  if (!record) {
    return (
      <div className="zen-card p-6 text-xs text-natural-text/60">
        No "costs" settings row yet — run <code>npm run setup</code> once so it gets seeded.
      </div>
    );
  }

  return (
    <div className="zen-card p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-base font-medium">Model prices</h3>
          <p className="text-[11px] text-natural-text/55 mt-1 leading-relaxed max-w-2xl">
            USD per generation, per model. The price is charged when a generation finishes —
            generations that fail on kie.ai's end are not billed and cost nothing, and reused
            library images cost nothing. Price changes apply to future generations only. Spend
            totals appear in the Statistics tab, visible to everyone.
          </p>
        </div>
        {saved && (
          <span className="zen-pill bg-emerald-50 text-emerald-700">
            <Check size={11} />
            Saved
          </span>
        )}
      </div>

      <div className="space-y-2">
        {current.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={row.model}
              onChange={(e) =>
                setRows(current.map((r, j) => (j === i ? { ...r, model: e.target.value } : r)))
              }
              className="zen-input text-xs font-mono w-64"
              placeholder="model id, e.g. veo3_fast"
            />
            <span className="text-xs text-natural-text/40">$</span>
            <input
              value={row.price}
              onChange={(e) =>
                setRows(current.map((r, j) => (j === i ? { ...r, price: e.target.value } : r)))
              }
              className="zen-input text-xs font-mono w-28"
              placeholder="0.00"
              inputMode="decimal"
            />
            <span className="text-[10px] text-natural-text/40">per generation</span>
            <button
              onClick={() => setRows(current.filter((_, j) => j !== i))}
              className="p-1.5 rounded-lg text-natural-text/40 hover:text-red-500 cursor-pointer"
              title="Remove this model"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-start gap-1.5">
          <AlertCircle size={13} className="shrink-0 mt-0.5" />
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => setRows([...current, { model: '', price: '' }])}
          className="zen-btn-ghost py-2 px-4 text-xs"
        >
          <Plus size={13} />
          Add model
        </button>
        <button
          onClick={() => void save()}
          disabled={busy || rows === null}
          className="zen-btn-primary py-2 px-4 text-xs"
        >
          <Save size={13} />
          {busy ? 'Saving…' : 'Save prices'}
        </button>
      </div>
    </div>
  );
}
